import type { Ref } from 'lit/directives/ref.js'

import { state, property } from 'lit/decorators.js'
import { createRef } from 'lit/directives/ref.js'
import { LitElement } from 'lit'

import {
	addSwipeEvent,
	type SwipeDestroyer,
	type SwipeSideEffects
} from '#utils/swipe'

type Mixin<T> = new (...args: any[]) => T

type CombinedMixins<T extends any[]> = T extends [infer First, ...infer Rest]
	? First & CombinedMixins<Rest>
	: unknown

/**
 * @description
 * Compose multiple mixins into a single class.
 *
 * @example
 * class MyElement extends compose(LfuiElement, WithDialog, WithInternals) {}
 */
export function compose<T extends any[]>(
	BaseClass: Mixin<LitElement>,
	...mixins: { [K in keyof T]: (superclass: Mixin<LitElement>) => T[K] }
): CombinedMixins<T> {
	return mixins.reduce(
		(acc, mixin) => mixin(acc),
		BaseClass
	) as CombinedMixins<T>
}

type DialogInterface = {
	[dialog]: Ref<HTMLDialogElement>
	close?(value?: string): void
}
export type DialogElement = LitElement & DialogInterface
export type DialogMixin = Mixin<DialogElement>

export const dialog = Symbol('dialog')

/**
 * @description
 * Elements implementing the dialog interface will have a reference to the
 * dialog element that the element is associated with and optionally a close
 * method.
 *
 * @example
 * class MyElement extends compose(LfuiElement, WithDialog) {
 *   close() {
 *     // Custom close logic
 *   }
 *   render() {
 *     return html`
 *       <dialog ${ref(this[dialog])}>
 *         <slot></slot>
 *       </dialog>
 *     `
 *   }
 * }
 */
export function WithDialog(Superclass: Mixin<LitElement>): DialogMixin {
	return class DialogElement extends Superclass {
		[dialog] = createRef<HTMLDialogElement>()
		close(value?: string) {
			this[dialog].value?.close(value)
		}
	}
}

type FormAssociatedInterface<
	T extends HTMLInputElement | HTMLButtonElement | HTMLTextAreaElement
> = {
	[internals]: ElementInternals
	[anchor]: Ref<T>
	get validity(): ValidityState
	get willValidate(): boolean
	get form(): HTMLFormElement | null
	checkValidity(): boolean
	reportValidity(): boolean
	validationMessage?: string
}

// Excuse the mess, this needed to properly forward the anchor element type
export type FormAssociatedElement<
	T extends HTMLInputElement | HTMLButtonElement | HTMLTextAreaElement
> = LitElement & FormAssociatedInterface<T>

export type FormAssociatedMixin<
	T extends HTMLInputElement | HTMLButtonElement | HTMLTextAreaElement
> = Mixin<FormAssociatedElement<T>>

export const internals = Symbol('internals')

export const anchor = Symbol('anchor')

/**
 * @description
 * Elements implementing the form associated interface will have a reference to
 * the anchor element that the element is associated with and optionally a
 * validation message.
 */
export function WithInternals<
	T extends
		| HTMLInputElement
		| HTMLButtonElement
		| HTMLTextAreaElement
		| HTMLSelectElement
>(Superclass: Mixin<LitElement>): FormAssociatedMixin<T> {
	class FormAssociatedElement extends Superclass {
		[internals]: ElementInternals;

		[anchor] = createRef<T>()

		@property({ attribute: 'validation-message' }) validationMessage: string

		static formAssociated = true

		constructor(...args: any[]) {
			super(...args)
			this[internals] = this.attachInternals()
		}

		updated(changed) {
			super.updated(changed)
			const el = this[anchor].value
			if (el) {
				const message = this.validationMessage || el.validationMessage
				this[internals].setValidity(el.validity, message, el)
			}
		}

		get validity() {
			return this[internals].validity
		}

		get willValidate() {
			return this[internals].willValidate
		}

		get form() {
			return this[internals].form
		}

		checkValidity() {
			return this[internals].checkValidity()
		}

		reportValidity() {
			return this[internals].reportValidity()
		}
	}

	return FormAssociatedElement
}

type SubmitterInterface = {
	href?: string
	type?: 'button' | 'submit' | 'reset' | 'menu'
	value?: string | number
	name?: string
	delegatedEvents?: string[]
	handleClick?: (event: PointerEvent) => void
}

export type SubmitterElement = LitElement & SubmitterInterface
export type SubmitterMixin = Mixin<SubmitterElement>

/**
 * @description
 * Elements implementing the submitter interface can attach a click handler that
 * will submit the form when the element is clicked.
 *
 * @example
 * class MyElement extends compose(LfuiElement, AsSubmitter) {
 *   render() {
 *     return html`
 *       <button type="submit" ${delegatedEvents(this.delegatedEvents, {
 *         after: { click: this.handleClick }
 *       })}>
 *         Submit
 *       </button>
 *     `
 *   }
 * }
 */
export function AsSubmitter(Superclass: Mixin<LitElement>): SubmitterMixin {
	return class SubmitterElement extends WithInternals<HTMLButtonElement>(
		Superclass
	) {
		declare href?: string
		declare type?: 'button' | 'submit' | 'reset' | 'menu'
		declare value?: string
		declare name?: string

		#hiddenButton: HTMLButtonElement | null = null

		delegatedEvents = [
			'click',
			'dblclick',
			'keydown',
			'keyup',
			'keypress',
			'focus',
			'blur',
			'focusin',
			'focusout',
			'contextmenu',
			'wheel',
			'auxclick'
		]

		/**
		 * Sync a hidden native submit button next to the custom element so that
		 * the browser's implicit form submission (pressing Enter in an input)
		 * works. Custom elements are not recognised as submit buttons by the
		 * browser, so we need a native <button type="submit"> in the form's
		 * light DOM.
		 */
		#syncHiddenButton() {
			const isSubmit =
				this.href == null && (this.type == null || this.type === 'submit')

			if (isSubmit && this[internals].form) {
				if (!this.#hiddenButton) {
					this.#hiddenButton = document.createElement('button')
					this.#hiddenButton.type = 'submit'
					this.#hiddenButton.hidden = true
					this.#hiddenButton.style.display = 'none'
					this.#hiddenButton.setAttribute('aria-hidden', 'true')
					this.#hiddenButton.setAttribute('tabindex', '-1')
					this.#hiddenButton.addEventListener('click', (event) => {
						// Prevent the browser's native form submission from the hidden
						// button so that handleClick can submit with the submitter polyfill
						event.preventDefault()

						// Pass a fresh event since the original has defaultPrevented=true
						// which would cause handleClick to bail out
						this.handleClick(new PointerEvent('click'))
					})
				}

				// Place the hidden button right after the custom element
				if (this.#hiddenButton.parentNode !== this.parentNode) {
					this.after(this.#hiddenButton)
				}
			} else {
				this.#hiddenButton?.remove()
				this.#hiddenButton = null
			}
		}

		connectedCallback() {
			super.connectedCallback()
			this.#syncHiddenButton()
		}

		updated(changed: Map<string, unknown>) {
			super.updated(changed)
			this.#syncHiddenButton()
		}

		disconnectedCallback() {
			super.disconnectedCallback()
			this.#hiddenButton?.remove()
			this.#hiddenButton = null
		}

		handleClick = (event: PointerEvent) => {
			if (!event.defaultPrevented && this.href == null) {
				if (this.type === 'reset') {
					this[internals].form?.reset()
				} else if (this.type == null || this.type === 'submit') {
					// Handle <form method="dialog">
					if (this[internals].form?.method === 'dialog') {
						// Look for ancestor implementing the dialog interface
						let parent = this.parentNode as DialogElement
						while (parent) {
							const dialogRef = parent[dialog]
							if (dialogRef) {
								// Get the closable object from the host or the dialog element
								const target = parent.close ? parent : dialogRef.value

								// Set the return value
								const value =
									this.value == null ? undefined : String(this.value)

								// Close the dialog
								target.close(value)

								// Prevent default form submission behavior
								return
							}

							parent = parent.parentNode as DialogElement
						}
					}

					// Polyfill the submitter property for the submit event
					this[internals].form?.addEventListener(
						'submit',
						(submitEvent) => {
							Object.defineProperty(submitEvent, 'submitter', {
								configurable: true,
								enumerable: true,
								value: this
							})
						},
						{ capture: true, once: true }
					)

					// Set the form value
					const data = new FormData()
					if (this.name && this.value != null) {
						data.set(this.name, this.value)
						this[internals].setFormValue(data)
					}

					// Submit the form
					this[internals].form?.requestSubmit()
				}
			}
		}
	}
}

type FocusableInterface = {
	focusVisible: boolean
}
export type FocusableElement = LitElement & FocusableInterface
export type FocusableMixin = Mixin<FocusableElement>

const focusable = new Set<FocusableElement>()

document.addEventListener('keydown', (event) => {
	if (event.key === 'Tab') {
		for (const element of focusable) {
			element.focusVisible = true
		}
	}
})

document.addEventListener('pointerdown', () => {
	for (const element of focusable) {
		element.focusVisible = false
	}
})

/**
 * @description
 * Elements implementing the focusable interface will have a state property that
 * indicates if the element is focused using keyboard navigation. This can be
 * used with the :focus-visible pseudo-class to apply enhanced focus styles.
 */
export function WithFocus(Superclass: Mixin<LitElement>): FocusableMixin {
	class FocusableElement extends Superclass {
		@state() focusVisible = false

		connectedCallback() {
			super.connectedCallback()
			focusable.add(this)
		}

		disconnectedCallback() {
			super.disconnectedCallback()
			focusable.delete(this)
		}
	}
	return FocusableElement
}

export type SwipeGesture = {
	/** The swipe direction that triggers dismiss */
	direction: 'swipedown' | 'swiperight'
	/** The element that listens for touch events */
	handle: HTMLElement
	/** Optional media query — gesture is only active when it matches. Always active if omitted. */
	media?: string
	/** Optional scrollable element — when touch starts inside it and it's scrolled, native scroll takes priority over dismiss */
	scrollContainer?: HTMLElement
	/** When true, the gesture is not set up. Useful for conditionally disabling (e.g. when resizable handles its own drag). */
	disabled?: boolean
}

type SwipeDismissConfig = {
	target: HTMLElement
	gestures: SwipeGesture[]
}

type SwipeDismissInterface = {
	get [swipeDismiss](): SwipeDismissConfig
}
export type SwipeDismissElement = LitElement & SwipeDismissInterface
export type SwipeDismissMixin = Mixin<SwipeDismissElement>

export const swipeDismiss = Symbol('swipeDismiss')

/**
 * @description
 * Elements implementing the swipe dismiss interface will respond to touch
 * swipe gestures by translating the target element and closing.
 *
 * The consuming class must implement `[swipeDismiss]` returning
 * `{ target, gestures }` where `target` is the element to animate/translate,
 * and `gestures` is an array of gesture configs each specifying a direction,
 * handle element, and optional media query.
 *
 * When a `media` string is provided on a gesture, that gesture is only active
 * when the media query matches. This allows responsive swipe behavior —
 * e.g. swipe-down on mobile only, or swipe-right on desktop only.
 *
 * Requires the class to also implement `close()` (e.g. via WithDialog).
 *
 * @example
 * class MySheet extends compose(LfuiElement, WithDialog, WithSwipeDismiss) {
 *   @query('.header-wrapper') private headerEl!: HTMLElement
 *   @query('.swipe-edge') private edgeEl!: HTMLElement
 *   @query('dialog') private dialogEl!: HTMLElement
 *
 *   get [swipeDismiss]() {
 *     return {
 *       target: this.dialogEl,
 *       gestures: [
 *         { direction: 'swipedown', handle: this.headerEl, media: '(max-width: 599px)' },
 *         { direction: 'swiperight', handle: this.edgeEl, media: '(min-width: 600px)' },
 *       ]
 *     }
 *   }
 * }
 */
export function WithSwipeDismiss(
	Superclass: Mixin<LitElement>
): SwipeDismissMixin {
	class SwipeDismissElement extends Superclass {
		[swipeDismiss]: SwipeDismissConfig

		#gestureSubscriptions: Array<{
			gesture: SwipeGesture
			mediaQuery?: MediaQueryList
			destroyer?: SwipeDestroyer
			handle?: HTMLElement
			onMediaChange?: (e: MediaQueryListEvent | MediaQueryList) => void
		}> = []

		#setupGesture(gesture: SwipeGesture, subscriptionIndex: number) {
			const { target } = this[swipeDismiss]
			const { direction, handle, scrollContainer } = gesture
			if (!handle || !target) return

			const sub = this.#gestureSubscriptions[subscriptionIndex]
			sub.handle = handle

			const isHorizontal = direction === 'swiperight'
			const axis = isHorizontal ? 'X' : 'Y'

			const displacement = (pos: any) =>
				isHorizontal ? pos.x - pos.startX : pos.y - pos.startY

			// Scroll-aware engagement tracking:
			// undefined = not yet decided, true = swipe engaged, false = let native scroll handle it
			let engaged: boolean | undefined = undefined
			let touchInScroller = false

			const setTransform = (value: number) => {
				target.style.setProperty('transform', `translate${axis}(${value}px)`)
			}

			const snapBack = () => {
				target.style.setProperty(
					'transition',
					'transform 0.25s cubic-bezier(0.2, 0.9, 0.3, 1)'
				)
				target.style.removeProperty('transform')
				target.addEventListener(
					'transitionend',
					() => {
						target.style.removeProperty('will-change')
						target.style.removeProperty('transition')
					},
					{ once: true }
				)
			}

			const sideEffects: SwipeSideEffects = {
				onSwipeStart: (e) => {
					engaged = undefined
					touchInScroller = scrollContainer
						? scrollContainer.contains(e.target as Node)
						: false

					// If touch started inside a scrolled container, bail immediately
					if (touchInScroller && scrollContainer) {
						const scrollPos = isHorizontal
							? scrollContainer.scrollLeft
							: scrollContainer.scrollTop
						if (scrollPos > 1) {
							engaged = false
							return
						}
					}
				},
				onSwipeMove: (e, positions) => {
					if (engaged === false) return

					// First touchmove — decide whether to engage
					if (engaged === undefined) {
						if (touchInScroller && displacement(positions) <= 0) {
							// Swiping into content (e.g. scrolling down) — let native scroll handle it
							engaged = false
							return
						}
						engaged = true
						target.style.setProperty('will-change', 'transform')
						target.style.setProperty('transition', 'none')
					}

					e.preventDefault()

					const amount = Math.max(0, displacement(positions))

					setTransform(amount)
				},
				onSwipeEnd: (_, positions, eventtype) => {
					const wasEngaged = engaged
					engaged = undefined

					if (!wasEngaged) return

					if (!positions) {
						target.style.removeProperty('will-change')
						target.style.removeProperty('transition')
						return
					}

					const d = displacement(positions)
					const targetDist = isHorizontal
						? window.innerWidth
						: window.innerHeight

					// Require minimum 20px drag before considering dismiss
					const shouldDismiss =
						d > 20 && (eventtype === direction || d > targetDist * 0.3)

					if (shouldDismiss) {
						const reducedMotion = matchMedia(
							'(prefers-reduced-motion: reduce)'
						).matches

						if (reducedMotion) {
							target.style.removeProperty('transform')
							target.style.removeProperty('transition')
							target.style.removeProperty('will-change')
							this.#closeHandler()
						} else {
							// CSS transition for dismiss — GPU-composited, smooth on mobile
							const progress = d / targetDist
							const duration = Math.round(Math.max(80, (1 - progress) * 250))
							target.style.setProperty(
								'transition',
								`transform ${duration}ms cubic-bezier(0.4, 0, 1, 1)`
							)
							setTransform(targetDist)

							target.addEventListener(
								'transitionend',
								() => {
									target.style.removeProperty('transform')
									target.style.removeProperty('transition')
									target.style.removeProperty('will-change')
									this.#closeHandler()
								},
								{ once: true }
							)
						}
					} else {
						snapBack()
					}
				}
			}

			sub.destroyer = addSwipeEvent(handle, direction, sideEffects)
		}

		#teardownGesture(subscriptionIndex: number) {
			const sub = this.#gestureSubscriptions[subscriptionIndex]
			if (!sub) return

			sub.destroyer?.destroy()
			sub.destroyer = undefined
			sub.handle = undefined
		}

		#closeHandler = () => (this as any).close?.()

		firstUpdated(changed: Map<string, unknown>) {
			super.firstUpdated?.(changed)

			const { target, gestures } = this[swipeDismiss]
			if (!gestures || !target) return

			gestures.forEach((gesture, index) => {
				const sub: {
					gesture: SwipeGesture
					mediaQuery?: MediaQueryList
					destroyer?: SwipeDestroyer
					handle?: HTMLElement
					onMediaChange?: (e: MediaQueryListEvent | MediaQueryList) => void
				} = { gesture }
				this.#gestureSubscriptions.push(sub)

				if (gesture.disabled) return

				if (gesture.media) {
					const mq = matchMedia(gesture.media)
					sub.mediaQuery = mq
					sub.onMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
						this.#teardownGesture(index)
						if (e.matches) {
							this.#setupGesture(gesture, index)
						}
					}
					mq.addEventListener('change', sub.onMediaChange)
					// Check initial state
					sub.onMediaChange(mq)
				} else {
					// No media query — always active
					this.#setupGesture(gesture, index)
				}
			})
		}

		disconnectedCallback() {
			super.disconnectedCallback()
			this.#gestureSubscriptions.forEach((sub, index) => {
				this.#teardownGesture(index)
				if (sub.mediaQuery && sub.onMediaChange) {
					sub.mediaQuery.removeEventListener('change', sub.onMediaChange)
				}
			})
			this.#gestureSubscriptions = []
		}
	}
	return SwipeDismissElement
}

type LangInterface = {
	lang: string
	[lang]: string
	t(translations: Record<string, Record<string, string>>, key: string): string
}
export type LangElement = LitElement & LangInterface
export type LangMixin = Mixin<LangElement>

export const lang = Symbol('lang')

/**
 * @description
 * Elements implementing the lang interface gain a reactive `lang` property
 * and a `t()` helper for looking up translated strings.
 *
 * The effective language is resolved in priority order:
 * 1. The element's own `lang` attribute/property
 * 2. The nearest ancestor element with a `[lang]` attribute (walks from parentElement)
 * 3. `document.documentElement.lang`
 * 4. `'sv'` as the ultimate fallback
 *
 * A MutationObserver watches for `lang` attribute changes on ancestor elements.
 * When an ancestor's `lang` changes, the component automatically re-renders
 * with the updated language.
 *
 * **Note:** `closest('[lang]')` cannot pierce shadow root boundaries upward.
 * If this element is inside another component's shadow DOM, only ancestors
 * within that same shadow root are checked before falling back to
 * `document.documentElement.lang`.
 *
 * The `t()` helper expects a translations object that always includes an `'sv'`
 * key — it is used as the fallback dictionary when the resolved language has no
 * matching entry.
 *
 * @example
 * const translations = {
 *   sv: { close: 'Stäng' },
 *   en: { close: 'Close' },
 * }
 *
 * class MyElement extends compose(LfuiElement, WithLang) {
 *   render() {
 *     return html`<button>${this.t(translations, 'close')}</button>`
 *   }
 * }
 */
export function WithLang(Superclass: Mixin<LitElement>): LangMixin {
	class LangMixinElement extends Superclass {
		@property({
			reflect: true,
			converter: { toAttribute: (v: string) => v || null }
		})
		lang: string = ''

		#langObserver: MutationObserver | null = null

		get [lang](): string {
			return (
				this.lang ||
				(this.parentElement?.closest('[lang]') as HTMLElement)?.lang ||
				document.documentElement.lang ||
				'sv'
			)
		}

		t(
			translations: Record<string, Record<string, string>>,
			key: string
		): string {
			const effectiveLang = this[lang]
			const short = effectiveLang.split('-')[0]
			const dict = translations[short] ?? translations['sv']
			return dict?.[key] ?? key
		}

		connectedCallback() {
			super.connectedCallback()
			this.#langObserver = new MutationObserver(this.#onLangMutation)
			this.#langObserver.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ['lang'],
				subtree: true
			})
		}

		disconnectedCallback() {
			super.disconnectedCallback()
			this.#langObserver?.disconnect()
			this.#langObserver = null
		}

		#onLangMutation = (mutations: MutationRecord[]) => {
			// Only re-render if the mutation is on an ancestor of this element
			// (or on documentElement itself)
			for (const mutation of mutations) {
				const target = mutation.target as HTMLElement
				if (target.contains(this)) {
					this.requestUpdate()
					break
				}
			}
		}
	}
	return LangMixinElement
}
